import * as Sentry from "@sentry/react";
import React from "react";
import { Navigate } from "react-router-dom";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import Spacer from "../../../components/Spacer";
import CreateEditRole from "./CreateEditRole";
import RoleDetail from "./RoleDetail";
import RolesTab from "./RolesTab";

import { useCompanyFields, useCurrentUser, useRbacPrimaryRolePermissions } from "../../../api/hooks";
import { useRoleFieldEnabled } from "../../../hooks/useFeatureFlag";

import { rbacPermissionsQuery } from "../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../api/queries/dataLoader";

import AddEmployeesPage from "./RoleDetail/EmployeesTab/AddEmployeesPage";

interface RolesAdminProps {
	tab: "roles" | "create" | "edit" | "detail" | "add-employees";
}

export const rolesLoader = () => {
	const queries: Array<() => SimplifiedQuery> = [() => rbacPermissionsQuery("roles")];

	return dataLoader([queries]);
};

const RolesAdmin = (props: RolesAdminProps) => {
	const { tab } = props;
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("roles");

	const { value: roleFieldEnabled, query: roleFieldFFQuery } = useRoleFieldEnabled();

	const { data: currentUser, isLoading: isUserLoading, isError: isUserError } = useCurrentUser();
	const companyId = currentUser?.me?.authorization?.payload?.company_id;
	const {
		data: companyFields,
		isLoading: isFieldsLoading,
		isError: isFieldsError,
	} = useCompanyFields(companyId || "");
	const hasRoleField = !!companyFields?.some((field) => field.type === "role");

	// isUserLoading must be included: while the current user is in flight the fields query is
	// DISABLED (no companyId) and reports isLoading false, so without it a cold load redirects
	// entitled companies before hasRoleField can resolve.
	const isLoadingAny = isRbacLoading || isUserLoading || isFieldsLoading || roleFieldFFQuery.isLoading;

	// A failed gate query leaves roleFieldEnabled/hasRoleField at their falsy fallbacks —
	// indistinguishable from a genuine "gated off". Redirecting on that would bounce entitled
	// companies, so show an error state instead of navigating away.
	const hasGateError = isUserError || isFieldsError || roleFieldFFQuery.isError;

	const hasViewPermission = rbacPermissions?.includes("roles:viewAdmin");

	const renderInfoBox = (heading: string, message: string) => (
		<div>
			<AdminTitle includeSpacer={false}>{heading}</AdminTitle>
			<div className="bg-py-primary-1 flex w-[400px] gap-1.5 rounded-sm p-5">
				<FontAwesomeIcon icon={faInfoCircle} className="text-py-primary-7 text-lg" />
				<div className="font-semibold">{message}</div>
			</div>
		</div>
	);

	const renderGatedContent = (): React.ReactNode => {
		if (isLoadingAny) return null;
		if (!hasViewPermission) {
			return renderInfoBox("Roles", "Missing required permissions.");
		}
		if (hasGateError) {
			return renderInfoBox("Roles", "Something went wrong loading this page. Refresh to try again.");
		}
		if (!roleFieldEnabled || !hasRoleField) {
			return <Navigate to="/administration" replace />;
		}
		switch (tab) {
			case "roles":
				return (
					<>
						<Spacer height={4} />
						<RolesTab />
					</>
				);
			case "create":
				return <CreateEditRole mode="create" />;
			case "edit":
				return <CreateEditRole mode="edit" />;
			case "detail":
				return <RoleDetail />;
			case "add-employees":
				return <AddEmployeesPage />;
		}
	};

	const isFormPage = tab === "create" || tab === "edit";
	const useFullBleed = isFormPage && !isLoadingAny && hasViewPermission && roleFieldEnabled && hasRoleField;

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			{useFullBleed ? (
				<>
					<div className="fixed inset-0 -z-10 bg-neutral-1" aria-hidden="true" />
					{renderGatedContent()}
				</>
			) : (
				<AdminContainer>{renderGatedContent()}</AdminContainer>
			)}
		</Sentry.ErrorBoundary>
	);
};

export default RolesAdmin;
